import {
  type AddExpenseRequest,
  type AddExpenseRequestAPI,
  type BaseExpense,
  type BaseExpenseAPI,
  type BulkUpdateExpensesRequest,
  type BulkUpdateExpensesRequestAPI,
  type BulkUpdateExpensesResponse,
  type BulkUpdateExpensesResponseAPI,
  ExpenseStatusEnum,
  type ExportExpensesRequest,
  type ExportExpensesRequestAPI,
  type GetExpenseResponse,
  type GetExpenseResponseAPI,
  type GetExpensesConfigResponse,
  type GetExpensesConfigResponseAPI,
  type GetExpensesResponse,
  type GetExpensesResponseAPI,
  type UpdateExpenseRequest,
  type UpdateExpenseRequestAPI,
} from '@omnipresentgroup/types';

const transformBaseExpenseFromAPI = (
  response: BaseExpenseAPI,
): BaseExpense => ({
  category: response.category,
  amount: response.amount,
  status: response.reported ? ExpenseStatusEnum.PROCESSED : response.status,
  id: response.id,
  employeeId: response.employee_id,
  employeeName: response.employee_name,
  approverId: response.approver_id,
  approverName: response.approver_name,
  title: response.title,
  description: response.description,
  dateSpent: response.date_spent,
  statusHistory: response.status_history?.map((history) => ({
    statusChangedAt: history.status_changed_at,
    statusChangedBy: history.status_changed_by,
    requestedChange: history.requested_change,
    status: history.status,
  })),
});

export const transformGetExpenseResponseFromAPI = (
  response: GetExpenseResponseAPI,
): GetExpenseResponse => ({
  ...transformBaseExpenseFromAPI(response),
  requestedChange: response.requested_change,
  rejectionReason: response.rejection_reason,
  approverSeen: response.approver_seen,
  documentId: response.document_id,
  createdAt: response.created_at,
  archivedAt: response.archived_at,
});

export const transformGetExpensesResponseFromAPI = (
  response: GetExpensesResponseAPI,
): GetExpensesResponse => ({
  items: response.items.map((item) => transformBaseExpenseFromAPI(item)),
  size: response.size,
  page: response.page,
  pages: response.pages,
  total: response.total,
});

export const transformExpenseToAPI = (
  expense: Partial<BaseExpense>,
): Partial<BaseExpenseAPI> => ({
  category: expense.category,
  amount: expense.amount,
  status: expense.status,
  id: expense.id,
  employee_id: expense.employeeId,
  employee_name: expense.employeeName,
  approver_id: expense.approverId,
  approver_name: expense.approverName,
  title: expense.title,
  description: expense.description,
  date_spent: expense.dateSpent,
  status_history: expense.statusHistory?.map((history) => ({
    status_changed_at: history.statusChangedAt,
    status_changed_by: history.statusChangedBy,
    requested_change: history.requestedChange,
    status: history.status,
  })),
});

export const transformExportExpensesRequestToAPI = (
  request: ExportExpensesRequest,
): ExportExpensesRequestAPI => ({
  approver_id: request.approverId,
  expense_ids: request.expenseIds,
  include_attachments: request.includeAttachments,
  export_format: request.exportFormat,
});

export const transformUpdateExpenseRequestToAPI = (
  request: UpdateExpenseRequest,
): UpdateExpenseRequestAPI => {
  const { amount, ...updateRequest } = request;
  return {
    ...transformExpenseToAPI(updateRequest),
    approved: request.approved,
    amount: request.amount,
    currency: request.currency,
    requested_change: request.requestedChange,
    rejection_reason: request.rejectionReason,
    document_id: request.documentId,
  };
};

export const transformAddExpenseRequestToAPI = (
  request: AddExpenseRequest,
): AddExpenseRequestAPI => ({
  approver_id: request.approverId,
  document_id: request.documentId,
  employee_id: request.employeeId,
  employee_name: request.employeeName,
  title: request.title,
  category: request.category,
  date_spent: request.dateSpent,
  description: request.description,
  amount: request.amount,
  currency: request.currency,
});

export const transformBulkUpdateExpensesRequestToAPI = (
  request: BulkUpdateExpensesRequest,
): BulkUpdateExpensesRequestAPI => ({
  expenses: request.expenses.map((expense) => ({
    expense_id: expense.expenseId,
    approved: expense.approved,
  })),
});

export const transformBulkUpdateExpensesResponseFromAPI = (
  response: BulkUpdateExpensesResponseAPI,
): BulkUpdateExpensesResponse =>
  response.map((item) => ({
    success: item.success,
    error: item.error,
    result: item.result ? transformBaseExpenseFromAPI(item.result) : undefined,
  }));

export const transformGetExpensesConfigFromAPI = (
  response: GetExpensesConfigResponseAPI,
): GetExpensesConfigResponse => {
  return {
    editingMethod: response.editing_method,
  };
};
